/**
 * IngredientMentionsSidebar — surfaces every encyclopedia entry that is
 * mentioned in the current article's body.
 *
 * Route: rendered inside FoodPulseArticlePage on desktop as a sticky
 * sidebar; collapses to an inline "Ingredients in this story" card on
 * mobile.
 *
 * Backend: GET /api/ingredients/mentioned-in/:articleSlug
 *
 * If zero ingredients are matched (e.g. the article is unrelated to the
 * encyclopedia), the whole component renders nothing — the parent page
 * stays clean.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FlaskConical, ChevronRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABEL = {
  "preservative":      "Preservative",
  "colour":            "Colour",
  "sweetener":         "Sweetener",
  "flavour-enhancer":  "Flavour Enhancer",
  "emulsifier":        "Emulsifier",
  "thickener":         "Thickener / Stabiliser",
  "mineral":           "Mineral",
};

export default function IngredientMentionsSidebar({ articleSlug, items: itemsProp }) {
  const [items, setItems] = useState(itemsProp || []);
  const [loading, setLoading] = useState(!itemsProp);

  useEffect(() => {
    // Parent already passed the fetched mentions — nothing else to do.
    if (itemsProp) { setItems(itemsProp); setLoading(false); return; }
    if (!articleSlug) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/ingredients/mentioned-in/${articleSlug}`);
        if (cancelled) return;
        setItems(res.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [articleSlug, itemsProp]);

  if (loading || items.length === 0) return null;

  // Group by category so the sidebar reads as a mini table-of-contents
  const groups = items.reduce((acc, it) => {
    const key = it.category || "other";
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});
  const groupKeys = Object.keys(groups);

  return (
    <aside
      className="rounded-2xl border bg-white overflow-hidden"
      style={{ borderColor: "#E9D8B4" }}
      data-testid="ingredient-mentions-sidebar"
    >
      <div
        className="px-4 py-3 border-b flex items-center gap-2"
        style={{ borderColor: "#E9D8B4", background: "linear-gradient(90deg,#FFF5DA 0%,#FBF7F0 100%)" }}
      >
        <FlaskConical className="w-4 h-4" style={{ color: "#B25D38" }} />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "#B25D38" }}>
            Also in this article
          </div>
          <div className="font-display text-sm" style={{ color: "#1A1613" }}>
            {items.length} ingredient{items.length === 1 ? "" : "s"} explained
          </div>
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto divide-y" style={{ borderColor: "#F3E7CB" }}>
        {groupKeys.map((cat) => (
          <div key={cat} className="p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2 px-1">
              {CATEGORY_LABEL[cat] || cat}
            </div>
            <ul className="space-y-1">
              {groups[cat].map((it) => (
                <li key={it.slug}>
                  <Link
                    to={`/food-pulse/encyclopedia/${it.slug}`}
                    className="group flex items-start gap-2 px-2 py-2 rounded-lg hover:bg-[#FFF5DA] transition-colors"
                    data-testid={`ingredient-mention-${it.slug}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold truncate" style={{ color: "#1A1613" }}>
                          {it.name}
                        </span>
                        {it.e_number && (
                          <span
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                            style={{ background: "#F0E2C7", color: "#8B5E10" }}
                          >
                            {it.e_number}
                          </span>
                        )}
                      </div>
                      {it.summary && (
                        <div className="text-[11px] opacity-70 leading-snug mt-0.5 line-clamp-2">
                          {it.summary}
                        </div>
                      )}
                    </div>
                    <ChevronRight
                      className="w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all flex-none mt-0.5"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="px-4 py-3 border-t text-center" style={{ borderColor: "#E9D8B4", background: "#FBF7F0" }}>
        <Link
          to="/food-pulse/encyclopedia"
          className="text-xs font-semibold underline hover:no-underline"
          style={{ color: "#B25D38" }}
          data-testid="ingredient-mentions-see-all"
        >
          Browse the full Ingredient Encyclopedia →
        </Link>
      </div>
    </aside>
  );
}
